import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { BookMarked, Printer, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Manual body styling — scoped to .ptc-manual (no typography plugin in this app).
const MANUAL_CSS = `
.ptc-manual { color: #1e293b; line-height: 1.65; font-size: 0.95rem; }
.ptc-manual h1 { font-size: 1.6rem; font-weight: 700; margin: 0 0 0.75rem; }
.ptc-manual h2 { font-size: 1.2rem; font-weight: 700; margin: 2rem 0 0.6rem; padding-top: 1rem; border-top: 1px solid #e2e8f0; scroll-margin-top: 1rem; }
.ptc-manual h3 { font-size: 1.05rem; font-weight: 600; margin: 1.5rem 0 0.5rem; scroll-margin-top: 1rem; }
.ptc-manual p { margin: 0.6rem 0; }
.ptc-manual ul { list-style: disc; padding-left: 1.4rem; margin: 0.6rem 0; }
.ptc-manual ol { list-style: decimal; padding-left: 1.4rem; margin: 0.6rem 0; }
.ptc-manual li { margin: 0.25rem 0; }
.ptc-manual hr { display: none; }
.ptc-manual strong { font-weight: 600; }
.ptc-manual code { background: #f1f5f9; border-radius: 4px; padding: 0.05rem 0.3rem; font-size: 0.85em; }
.ptc-manual table { border-collapse: collapse; margin: 1rem 0; font-size: 0.875rem; display: block; overflow-x: auto; }
.ptc-manual th, .ptc-manual td { border: 1px solid #e2e8f0; padding: 0.45rem 0.65rem; text-align: left; vertical-align: top; }
.ptc-manual th { background: #f8fafc; font-weight: 600; }
`;

const slug = (text, i) => `m-${i}-${text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`;

const AdminManualsPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [manuals, setManuals] = useState([]);
  const [activeKey, setActiveKey] = useState(() => {
    try { return localStorage.getItem('ptc-admin-manual') || 'internal'; } catch (e) { return 'internal'; }
  });
  const [manual, setManual] = useState(null);
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef(null);
  const [toc, setToc] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/manuals`, { headers })
      .then(r => setManuals(r.data.manuals || []))
      .catch(() => toast.error('Failed to load manuals'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    try { localStorage.setItem('ptc-admin-manual', activeKey); } catch (e) { /* ignore */ }
    axios.get(`${API_URL}/api/admin/manuals/${activeKey}`, { headers })
      .then(r => setManual(r.data))
      .catch(err => { setManual(null); toast.error(err.response?.data?.detail || 'Failed to load manual'); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  // Build the "On this page" list from the rendered headings.
  useEffect(() => {
    if (!bodyRef.current) { setToc([]); return; }
    const heads = Array.from(bodyRef.current.querySelectorAll('h2, h3'));
    heads.forEach((h, i) => { h.id = slug(h.textContent, i); });
    setToc(heads.map(h => ({ id: h.id, text: h.textContent, level: h.tagName })));
  }, [manual]);

  const updated = useMemo(() => (
    manual?.updated_at ? new Date(manual.updated_at * 1000).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }) : null
  ), [manual]);

  const handlePrint = () => {
    if (!manual) return;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Allow pop-ups for this site to print / save as PDF'); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${manual.title} Manual — Plane Tree Capital</title>
      <style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;max-width:780px;margin:32px auto;padding:0 16px;}
      ${MANUAL_CSS} .ptc-manual h2{break-after:avoid;}</style></head>
      <body><div class="ptc-manual">${manual.html}</div></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="space-y-6" data-testid="admin-manuals-page">
      <style>{MANUAL_CSS}</style>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BookMarked className="h-6 w-6" /> User Manuals</h1>
          <p className="text-sm text-slate-500">Portal manuals for staff, investors and clients.</p>
        </div>
        <Button onClick={handlePrint} disabled={!manual} data-testid="manual-print-btn">
          <Printer className="h-4 w-4 mr-2" /> Print / Save as PDF
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {manuals.map(m => (
          <Button
            key={m.key}
            variant={m.key === activeKey ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveKey(m.key)}
            disabled={!m.available}
            data-testid={`manual-tab-${m.key}`}
          >
            {m.title}
            <span className="ml-2 text-xs opacity-70 hidden sm:inline">{m.audience}</span>
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-6">
        <aside className="hidden lg:block">
          <div className="sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">On this page</div>
            <nav className="flex flex-col gap-0.5">
              {toc.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => document.getElementById(t.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className={`text-left text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded px-2 py-1 ${t.level === 'H3' ? 'pl-5 text-xs' : ''}`}
                >
                  {t.text}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        <Card>
          <CardContent className="p-6 sm:p-8">
            {loading && !manual ? (
              <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : manual ? (
              <>
                {updated && <p className="text-xs text-slate-400 mb-4">Last updated {updated}</p>}
                {/* Trusted content: our own docs/ files, rendered server-side with raw HTML escaped. */}
                <div ref={bodyRef} className="ptc-manual" dangerouslySetInnerHTML={{ __html: manual.html }} />
              </>
            ) : (
              <p className="text-slate-500">This manual isn't available.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminManualsPage;
